/**
 * Route-level deployment (Layer A) checks.
 *
 * The rest of Layer A is decided when the bundle is built — which nav sections exist, which
 * queries may run. Routes are the exception: every game's route files ship in every client's
 * bundle, so a typed or bookmarked URL can still reach a page for a game this client never
 * deployed, where the backend registered no endpoints and every request 404s. This is the
 * one check that has to run per visit.
 */
import { redirect } from '@tanstack/react-router';
import type { GameKey } from '@low6-pickem/platform-config';
import { isGameEnabled } from './platform-filters';

/**
 * Use in a route's `beforeLoad` to keep a game's pages unreachable unless the client
 * deployed it. Prefer a folder's `route.tsx` over its leaves, so pages added later inherit
 * the check rather than needing to remember it.
 *
 * Takes the platform's `GameKey` union — not a sports-config game type code, which cannot
 * describe lobby at all, and not a bare string, where a typo would compile and silently
 * redirect away from a page the client does deploy.
 *
 * This is a capability check, not a security control: it decides what the admin offers,
 * while authentication and authorisation decide what a request is allowed to do.
 */
export function requireGameEnabled(gameKey: GameKey): void {
  if (!isGameEnabled(gameKey)) {
    throw redirect({ to: '/dashboard' });
  }
}
